import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { User, UserListResponse, UserStats } from '../../types/user';
import UserForm from './UserForm';
import UserDetails from './UserDetails';
import DemoUserForm from './DemoUserForm';

export const UserManagement: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);

  // Status Tab selection: 'all' | 'active' | 'inactive' | 'locked'
  const [statusTab, setStatusTab] = useState<'all' | 'active' | 'inactive' | 'locked'>('all');

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });

  const [filters, setFilters] = useState({
    search: initialSearch,
    role: '' as 'USER' | 'ADMIN' | 'SUPER_ADMIN' | 'INSURER' | '',
    insurer_org_id: undefined as number | undefined,
  });

  const [insurerOrgs, setInsurerOrgs] = useState<Array<{ id: number; name: string }>>([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showDemoUserForm, setShowDemoUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [openActionDropdownId, setOpenActionDropdownId] = useState<number | null>(null);

  // Load insurer orgs
  useEffect(() => {
    const loadInsurerOrgs = async () => {
      try {
        const response = await adminService.listInsurerOrgs();
        setInsurerOrgs(response.insurers.map((org) => ({ id: org.id, name: org.name })));
      } catch (err) {
        console.error('Failed to load insurer organizations:', err);
      }
    };
    loadInsurerOrgs();
  }, []);

  // Load stats for tab counts
  const loadStats = useCallback(async () => {
    try {
      const statsData = await adminService.getUserStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load stats for tab counts:', err);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Load users
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Determine is_active filter from status tab
      let isActiveFilter: boolean | undefined = undefined;
      if (statusTab === 'active') isActiveFilter = true;
      if (statusTab === 'inactive') isActiveFilter = false;

      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        search: filters.search.trim() || undefined,
        role: filters.role === '' ? undefined : filters.role,
        is_active: isActiveFilter,
        insurer_org_id: filters.insurer_org_id,
      };

      const response: UserListResponse = await adminService.getUsers(params);
      let loadedUsers = response.users || [];

      // Filter for locked if selected
      if (statusTab === 'locked') {
        const now = new Date();
        loadedUsers = loadedUsers.filter(
          (u) => u.locked_until && new Date(u.locked_until) > now
        );
      }

      setUsers(loadedUsers);
      setPagination((prev) => ({
        ...prev,
        total: response.total,
        total_pages: response.total_pages,
      }));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, filters, statusTab]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = () => setOpenActionDropdownId(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const handleStatusTabChange = (tab: 'all' | 'active' | 'inactive' | 'locked') => {
    setStatusTab(tab);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleActivateUser = async (userId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await adminService.activateUser(userId);
      setSuccessMessage('User activated successfully');
      loadUsers();
      loadStats();
      setTimeout(() => setSuccessMessage(null), 3500);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to activate user');
    }
  };

  const handleUserAction = async (action: string, user: User, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setOpenActionDropdownId(null);

    try {
      switch (action) {
        case 'view':
          setSelectedUser(user);
          setShowUserDetails(true);
          break;
        case 'edit':
          setEditingUser(user);
          setShowUserForm(true);
          break;
        case 'activate':
          await adminService.activateUser(user.id);
          setSuccessMessage('User activated successfully');
          loadUsers();
          loadStats();
          setTimeout(() => setSuccessMessage(null), 3500);
          break;
        case 'deactivate':
          await adminService.deactivateUser(user.id);
          setSuccessMessage('User deactivated successfully');
          loadUsers();
          loadStats();
          setTimeout(() => setSuccessMessage(null), 3500);
          break;
        case 'delete':
          if (window.confirm(`Are you sure you want to delete ${user.email}?`)) {
            await adminService.deleteUser(user.id);
            setSuccessMessage('User deleted successfully');
            loadUsers();
            loadStats();
            setTimeout(() => setSuccessMessage(null), 3500);
          }
          break;
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const totalCount = stats?.total_users ?? pagination.total;
  const activeCount = stats?.active_users ?? 0;
  const inactiveCount = Math.max(0, totalCount - activeCount);
  const lockedCount = stats?.locked_users ?? 0;

  const formatLastLogin = (dateStr?: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatCreated = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getOrgDetails = (user: User) => {
    if (user.role === 'SUPER_ADMIN' || user.is_superuser) {
      return {
        name: 'Flash (internal)',
        roleTag: 'Platform staff',
      };
    }
    const org = user.insurer_org_id
      ? insurerOrgs.find((o) => o.id === user.insurer_org_id)?.name || 'Insurance Partner'
      : 'Direct client';

    if (user.role === 'INSURER') {
      return {
        name: org,
        roleTag: user.org_role === 'org_admin' ? 'Org admin' : 'Org member',
      };
    }
    return {
      name: org,
      roleTag: 'Client',
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Users</h1>
          <p className="mt-1 text-sm text-slate-500">
            Everyone with access to Flash, across clients, insurers and staff.
          </p>
        </div>
        <div className="flex items-center space-x-3 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setShowDemoUserForm(true)}
            className="px-3.5 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-800 bg-white hover:bg-slate-50 transition-colors shadow-sm"
          >
            Create demo user
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingUser(null);
              setShowUserForm(true);
            }}
            className="px-4 py-2 bg-[#991B1B] hover:bg-[#7F1D1D] text-white rounded-lg text-xs font-bold transition-colors shadow-sm flex items-center space-x-1.5"
          >
            <span>+</span>
            <span>Add user</span>
          </button>
        </div>
      </div>

      {/* Alert / Notification Messages */}
      {successMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-xs font-medium">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg text-xs font-medium flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-rose-500 font-bold ml-2">
            ✕
          </button>
        </div>
      )}

      {/* Main Table Card */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {/* Status Filter Tabs */}
        <div className="border-b border-slate-200 px-6 pt-4 flex space-x-8">
          <button
            type="button"
            onClick={() => handleStatusTabChange('all')}
            className={`pb-3 text-xs font-bold transition-colors flex items-center space-x-2 border-b-2 ${
              statusTab === 'all'
                ? 'border-[#991B1B] text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <span>All</span>
            <span
              className={`px-1.5 py-0.2 rounded-full text-[11px] ${
                statusTab === 'all'
                  ? 'bg-[#0F172A] text-white'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {totalCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => handleStatusTabChange('active')}
            className={`pb-3 text-xs font-bold transition-colors flex items-center space-x-2 border-b-2 ${
              statusTab === 'active'
                ? 'border-[#991B1B] text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <span>Active</span>
            <span
              className={`px-1.5 py-0.2 rounded-full text-[11px] ${
                statusTab === 'active'
                  ? 'bg-[#0F172A] text-white'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {activeCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => handleStatusTabChange('inactive')}
            className={`pb-3 text-xs font-bold transition-colors flex items-center space-x-2 border-b-2 ${
              statusTab === 'inactive'
                ? 'border-[#991B1B] text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <span>Inactive</span>
            <span
              className={`px-1.5 py-0.2 rounded-full text-[11px] ${
                statusTab === 'inactive'
                  ? 'bg-[#0F172A] text-white'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {inactiveCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => handleStatusTabChange('locked')}
            className={`pb-3 text-xs font-bold transition-colors flex items-center space-x-2 border-b-2 ${
              statusTab === 'locked'
                ? 'border-[#991B1B] text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <span>Locked</span>
            <span
              className={`px-1.5 py-0.2 rounded-full text-[11px] ${
                statusTab === 'locked'
                  ? 'bg-[#0F172A] text-white'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {lockedCount}
            </span>
          </button>
        </div>

        {/* Filter Toolbar */}
        <div className="p-6 pb-4 border-b border-slate-100 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          {/* Search */}
          <div className="md:col-span-5">
            <label className="block text-xs font-semibold text-slate-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Name or email"
              value={filters.search}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, search: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
          </div>

          {/* Role */}
          <div className="md:col-span-3">
            <label className="block text-xs font-semibold text-slate-700 mb-1">Role</label>
            <select
              value={filters.role}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, role: e.target.value as any }));
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg text-slate-900 bg-white focus:outline-none focus:ring-1 focus:ring-slate-400"
            >
              <option value="">All roles</option>
              <option value="USER">User</option>
              <option value="ADMIN">Admin</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="INSURER">Insurer</option>
            </select>
          </div>

          {/* Partner Org */}
          <div className="md:col-span-3">
            <label className="block text-xs font-semibold text-slate-700 mb-1">Partner org</label>
            <select
              value={filters.insurer_org_id ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  insurer_org_id: val === '' ? undefined : parseInt(val, 10),
                }));
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg text-slate-900 bg-white focus:outline-none focus:ring-1 focus:ring-slate-400"
            >
              <option value="">All organizations</option>
              {insurerOrgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          <div className="md:col-span-1 flex items-center justify-end pb-1.5">
            <button
              type="button"
              onClick={() => {
                setFilters({ search: '', role: '', insurer_org_id: undefined });
                setStatusTab('all');
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="text-xs font-semibold text-indigo-900 hover:text-indigo-700 whitespace-nowrap"
            >
              Clear filters
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          {loading && users.length === 0 ? (
            <div className="py-16 text-center text-slate-500 text-xs">
              <div className="w-6 h-6 border-2 border-slate-400 border-t-slate-800 rounded-full animate-spin mx-auto mb-2"></div>
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <div className="py-16 text-center text-slate-500 text-xs">
              No users found matching the selected filters.
            </div>
          ) : (
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-white text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="py-3 px-6">User</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Organization</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Last Login</th>
                  <th className="py-3 px-4">Created</th>
                  <th className="py-3 px-6 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => {
                  const initial =
                    user.full_name?.trim()[0]?.toUpperCase() ||
                    user.email[0]?.toUpperCase() ||
                    'U';
                  const orgInfo = getOrgDetails(user);

                  let roleBadgeStyle = 'bg-slate-100 text-slate-700';
                  let roleDisplayName = 'User';

                  if (user.role === 'SUPER_ADMIN' || user.is_superuser) {
                    roleBadgeStyle = 'bg-[#0F172A] text-white';
                    roleDisplayName = 'Super admin';
                  } else if (user.role === 'INSURER') {
                    roleBadgeStyle = 'bg-indigo-50 text-indigo-700';
                    roleDisplayName = 'Insurer';
                  } else if (user.role === 'ADMIN') {
                    roleBadgeStyle = 'bg-purple-50 text-purple-700';
                    roleDisplayName = 'Admin';
                  }

                  const isInactive = !user.is_active;

                  return (
                    <tr
                      key={user.id}
                      className="hover:bg-slate-50/70 transition-colors group cursor-pointer"
                      onClick={() => handleUserAction('view', user)}
                    >
                      {/* USER Column */}
                      <td className="py-3.5 px-6">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                              user.role === 'SUPER_ADMIN'
                                ? 'bg-[#0F172A] text-white'
                                : 'bg-slate-200 text-slate-700'
                            }`}
                          >
                            {initial}
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-slate-900">
                              {user.full_name ? (
                                user.full_name
                              ) : (
                                <span className="italic text-slate-500 font-normal">
                                  Name not set
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400 font-normal">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* ROLE Column */}
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${roleBadgeStyle}`}
                        >
                          {roleDisplayName}
                        </span>
                      </td>

                      {/* ORGANIZATION Column */}
                      <td className="py-3.5 px-4">
                        <div className="text-xs font-semibold text-slate-900">
                          {orgInfo.name}
                        </div>
                        <div className="text-[11px] text-slate-400 font-normal">
                          {orgInfo.roleTag}
                        </div>
                      </td>

                      {/* STATUS Column */}
                      <td className="py-3.5 px-4">
                        {user.is_active ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#DCFCE7] text-[#166534]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]"></span>
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#B45309]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#D97706]"></span>
                            Inactive
                          </span>
                        )}
                      </td>

                      {/* LAST LOGIN Column */}
                      <td className="py-3.5 px-4 text-slate-600 font-normal">
                        {formatLastLogin(user.last_login)}
                      </td>

                      {/* CREATED Column */}
                      <td className="py-3.5 px-4 text-slate-600 font-normal">
                        {formatCreated(user.created_at)}
                      </td>

                      {/* ACTIONS Column */}
                      <td className="py-3.5 px-6 text-right relative">
                        <div className="flex items-center justify-end space-x-2">
                          {isInactive && (
                            <button
                              type="button"
                              onClick={(e) => handleActivateUser(user.id, e)}
                              className="px-3 py-1 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-lg text-xs font-semibold transition-colors"
                            >
                              Activate
                            </button>
                          )}

                          <div className="relative">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenActionDropdownId((prev) =>
                                  prev === user.id ? null : user.id
                                );
                              }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-500 transition-colors"
                            >
                              <span className="leading-none text-base">···</span>
                            </button>

                            {openActionDropdownId === user.id && (
                              <div
                                className="absolute right-0 mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-30 text-xs text-left"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={(e) => handleUserAction('view', user, e)}
                                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50"
                                >
                                  View details
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => handleUserAction('edit', user, e)}
                                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50"
                                >
                                  Edit user
                                </button>
                                {user.is_active ? (
                                  <button
                                    type="button"
                                    onClick={(e) => handleUserAction('deactivate', user, e)}
                                    className="w-full text-left px-3 py-2 text-amber-600 hover:bg-amber-50"
                                  >
                                    Deactivate user
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={(e) => handleUserAction('activate', user, e)}
                                    className="w-full text-left px-3 py-2 text-emerald-600 hover:bg-emerald-50"
                                  >
                                    Activate user
                                  </button>
                                )}
                                {!user.is_superuser && (
                                  <button
                                    type="button"
                                    onClick={(e) => handleUserAction('delete', user, e)}
                                    className="w-full text-left px-3 py-2 text-rose-600 hover:bg-rose-50 border-t border-slate-100"
                                  >
                                    Delete user
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer Summary & Pagination */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <div>
            Showing {users.length} of {pagination.total} users
          </div>
          {pagination.total_pages > 1 && (
            <div className="flex items-center space-x-1">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                className="px-2.5 py-1 border border-slate-200 rounded-md disabled:opacity-40 hover:bg-slate-50"
              >
                Previous
              </button>
              <span className="px-2">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                type="button"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                className="px-2.5 py-1 border border-slate-200 rounded-md disabled:opacity-40 hover:bg-slate-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showUserForm && (
        <UserForm
          user={editingUser}
          onClose={() => {
            setShowUserForm(false);
            setEditingUser(null);
          }}
          onSuccess={() => {
            setShowUserForm(false);
            setEditingUser(null);
            loadUsers();
            loadStats();
          }}
        />
      )}

      {showDemoUserForm && (
        <DemoUserForm
          onClose={() => setShowDemoUserForm(false)}
          onSuccess={() => {
            setShowDemoUserForm(false);
            loadUsers();
            loadStats();
          }}
        />
      )}

      {showUserDetails && selectedUser && (
        <UserDetails
          user={selectedUser}
          onClose={() => {
            setShowUserDetails(false);
            setSelectedUser(null);
          }}
          onUserUpdated={() => {
            loadUsers();
            loadStats();
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;
